import { useMemo, useState } from "react";

import type { Attribute, FieldId, FieldValue } from "../../../types";

import {
  selectActiveOptions,
  selectIsEmpty,
  selectShouldCallApi,
  selectShouldUseDynamicOptions,
  selectSourceValue,
  selectStaticOptions,
} from "./DynamicSelectField.selector";
import {
  type FieldCode,
  useFieldDependencyResolve,
} from "./useFieldDependencyResolve";

interface useDynamicSelectFieldParams {
  fieldId: FieldId;
  field: Attribute;
  allValues: FieldValue[];
  sourceFieldCode: FieldCode;
  targetFieldCode: FieldCode;
}

/**
 * DynamicSelectFieldのビジネスロジックを管理するカスタムフック
 * 責務: データフェッチ、状態管理、ビジネスロジック
 */
export const useDynamicSelectField = ({
  fieldId,
  field,
  allValues,
  sourceFieldCode,
  targetFieldCode,
}: useDynamicSelectFieldParams) => {
  // 依存元フィールドの値を取得
  const sourceValue = useMemo(
    () => selectSourceValue(field, allValues, fieldId),
    [field, allValues, fieldId],
  );

  // 静的な選択肢を取得
  const staticOptions = useMemo(() => selectStaticOptions(field), [field]);

  // 静的な選択肢があるかどうかを判定
  const hasStaticOptions = staticOptions.length > 0;

  // 初期値の依存元フィールド値を記録（初回レンダリング時の値）
  const [beforeSourceValue, setBeforeSourceValue] = useState(sourceValue);

  // 依存元フィールドが変更されたかどうかを判定
  const hasSourceValueChanged = sourceValue !== beforeSourceValue;
  if (hasSourceValueChanged) {
    setBeforeSourceValue(sourceValue);
  }

  // 動的選択肢を使用すべきかを判定
  const shouldUseDynamicOptions = selectShouldUseDynamicOptions(
    hasStaticOptions,
    hasSourceValueChanged,
    sourceValue,
  );

  // APIコール条件を判定
  const shouldCallApi = selectShouldCallApi(
    shouldUseDynamicOptions,
    sourceFieldCode,
    targetFieldCode,
    sourceValue,
  );

  // 動的選択肢のAPIを呼び出し
  const dynamicApiResult = useFieldDependencyResolve(
    sourceFieldCode,
    targetFieldCode,
    sourceValue,
    { enabled: shouldCallApi },
  );

  // 使用する選択肢を決定
  const options = selectActiveOptions(
    shouldUseDynamicOptions,
    dynamicApiResult.options.map((option) => ({
      value: option.value,
      label: option.label,
      description: option.description ?? undefined,
    })),
    staticOptions,
  );

  // 選択肢が空かどうかを判定
  const isEmpty = selectIsEmpty(
    shouldUseDynamicOptions,
    dynamicApiResult.isEmpty,
    staticOptions,
  );

  // UIの状態を決定
  const uiState = useMemo(() => {
    // エラー状態
    if (dynamicApiResult.hasError) {
      return {
        type: "error" as const,
        error: dynamicApiResult.error,
      };
    }

    // 依存元が選択されていない状態
    if (!sourceValue && sourceFieldCode) {
      return {
        type: "waiting-dependency" as const,
      };
    }

    // 正常状態
    return {
      type: "ready" as const,
      isLoading: dynamicApiResult.isLoading,
      isEmpty,
      placeholder: dynamicApiResult.fieldConfig?.placeholder,
    };
  }, [
    dynamicApiResult.hasError,
    dynamicApiResult.error,
    dynamicApiResult.isLoading,
    dynamicApiResult.fieldConfig?.placeholder,
    sourceValue,
    sourceFieldCode,
    isEmpty,
  ]);

  return {
    options,
    uiState,
    sourceValue,
    hasSourceValueChanged,
  };
};
